<template>
  <div
    id="links-dashboard"
    :class="{ fullscreen: isFeatureFullscreen && !mobileScreen, mobile: mobileScreen }"
  >
    <OverlayCard
      v-if="showConfirmDelete"
      class="overlay-confirm-delete"
      :class="{
        'overlay-confirm-delete__mobile': mobileScreen,
        'overlay-confirm-delete__fullscreen': isFeatureFullscreen && !mobileScreen
      }"
      @closeOverlay="showConfirmDelete = false"
    >
      <ConfirmDeleteAttachmentCard
        :buttonText="t('general__text__delete') + ' ' + t('general__data__link(s)').toLowerCase()"
        @closeOverlay="showConfirmDelete = false"
        @confirmDelete="deleteLink()"
      />
    </OverlayCard>
    <div class="search-container">
      <InputSearch
        class="search-container__input"
        :clearInput="clearInput"
        :placeholderText="t('general__text__search-placeholder-links')"
        @emit-search-string="searchStringValue"
      />
    </div>
    <div class="include-child-object-container">
      <!-- Disabled checkbox for realm -->
      <CheckBoxStandard
        class="include-child-object-container__checkbox"
        :checked="includeChildObject"
        :disabled="selectedObject === 0"
        :title="t('general__text__objects-include-child')"
        @valueChanged="includeChildObject = !includeChildObject"
      />
      <InfoLabel
        class="include-child-object-container__info"
        :info="t('tooltip__links-include-child-objects')"
      />
    </div>
    <div class="table-container">
      <TableLinksMini
        :fields="fields"
        :items="linksList"
        @archiveLink="archiveLink"
        @deleteLink="showConfirmDelete = true"
        @loadMoreLinks="loadMoreLinks"
      />
    </div>
    <div
      class="bottom-container"
      :class="{ 'bottom-container__no-button': !store.getters.hasLinkCreatorAccess }"
    >
      <ButtonOutline
        v-if="store.getters.hasLinkCreatorAccess"
        :buttonText="t('general__data__link-add-new')"
        :iconLeft="iconPlusSmall"
        :size="'small'"
        style="width: 100%"
        @onClicked="showModalCreateLink"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import TableLinksMini from '@/components/features/attachments/TableLinksMini.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import InputSearch from '@/design-system/molecules/InputSearch.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import {
  archiveAttachments,
  deleteAttachments,
  AttachmentsPage,
  Attachment,
  getAttachmentsPage
} from '@/services/attachments.service'
import { orderAlphabetical } from '@/utils/sort'
import { debounce } from '@/utils/debounce'
import iconPlusSmall from '@/design-system/assets/icons/Icon-Plus-Small.svg'

type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'closeDashboard'): void
}
defineEmits<Emits>()
const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const linksList = computed(() => {
  return piniaStore.linksList
})
const selectedObject = computed(() => {
  return store.getters.selectedObject
})

const clearInput = ref(false)
const includeChildObject = ref(false)
const linksPage = ref<AttachmentsPage<Attachment>>()
const pageNumber = ref(0)
const searchString = ref('')
const showConfirmDelete = ref(false)
const showLinkMenu = ref(false)

const fields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: linksPage.value
        ? linksPage.value.totalElements + ' ' + t('general__data__links').toLocaleLowerCase()
        : ''
    }
  ]
  return fields
})
const linksUpdated = computed(() => {
  return piniaStore.linksUpdated
})
const mobileScreen = computed(() => {
  return store.state.mobileScreen
})
const isSearchActive = computed(() => {
  return searchString.value.length >= 1
})
async function archiveLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await archiveAttachments([id])
    // For future bulk operations, check "succeeded" array for each link instead of current "success" boolean
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
      showLinkMenu.value = false
    }
    if (!response?.data.success) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__link-archive-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__data__link-archive-failed')}, ${t(
        'general__text__retry-later-again'
      ).toLowerCase()}`
    })
    console.error(e)
  }
}
async function deleteLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await deleteAttachments([piniaStore.selectedLink.attachmentId])
    // For future bulk operations, check "succeeded" array for each link instead of current "success" boolean
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
      showLinkMenu.value = false
    }
    if (!response?.data.success) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__link-delete-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__data__link-delete-failed')}, ${t(
        'general__text__retry-later-again'
      ).toLowerCase()}`
    })
    console.error(e)
  }
}
async function getItems(entityId: string, page: number) {
  const linksArray: Attachment[] = []
  linksPage.value = await getAttachmentsPage(
    entityId,
    ['LINK'],
    page,
    25,
    'name,asc',
    null,
    includeChildObject.value
  )
  linksPage.value?.content.forEach((item) => {
    linksArray.push(item)
  })
  linksArray.sort(orderAlphabetical)
  piniaStore.setLinksList(linksArray)
}
async function getSearchItems(
  entityId: string,
  searchFilter: string,
  pageNumber: number,
  pageSize = 25
) {
  const linksArray: Attachment[] = []
  linksPage.value = await getAttachmentsPage(
    entityId,
    ['LINK'],
    pageNumber,
    pageSize,
    'name,asc',
    searchFilter,
    includeChildObject.value
  )
  linksPage.value?.content.forEach((item) => {
    linksArray.push(item)
  })
  linksArray.sort(orderAlphabetical)
  piniaStore.setLinksList(linksArray)
}
function loadMoreLinks() {
  pageNumber.value++
  if (pageNumber.value === linksPage.value?.totalPages) {
    return
  }
  if (isSearchActive.value) {
    getSearchItems(store.getters.selectedObjectId, searchString.value, pageNumber.value)
  } else {
    getItems(store.getters.selectedObjectId, pageNumber.value)
  }
}
function refreshList() {
  if (isSearchActive.value) {
    resetAndSearchLinks(searchString.value)
  } else {
    resetAndGetLinkList()
  }
}
const refreshListDebounce = debounce(() => {
  if (isSearchActive.value) {
    resetAndSearchLinks(searchString.value)
  } else {
    resetAndGetLinkList()
  }
}, 400)
function resetAndGetLinkList() {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setLinksList([])
  pageNumber.value = 0
  getItems(store.getters.selectedObjectId, pageNumber.value)
}
function resetAndSearchLinks(newVal: string) {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setLinksList([])
  pageNumber.value = 0
  getSearchItems(store.getters.selectedObjectId, newVal, 0)
}
function searchStringValue(searchValue: string) {
  if (searchValue.length >= 1) clearInput.value = false
  searchString.value = searchValue
}
function showModalCreateLink() {
  clearInput.value = true
  piniaStore.setSelectedLink(null)
  piniaStore.setCurrentOverlayModalPinia('editLink')
  piniaStore.setShowOverlayModalPinia(true)
}

watch(linksList, (newList, oldList) => {
  // remove one item from list when a link deleted
  if (!linksPage.value?.totalElements) return
  // Todo: The line below could make the totalElements value incorrect for the last page
  if (newList.length < oldList.length) linksPage.value.totalElements--
})
watch(
  linksUpdated,
  (newLinksArray, oldLinksArray) => {
    if (newLinksArray.length && newLinksArray.length > oldLinksArray.length) {
      piniaStore.setLinksList([])
      pageNumber.value = 0
      getItems(store.getters.selectedObjectId, pageNumber.value)
    }
  },
  { deep: true }
)
watch(includeChildObject, () => {
  refreshList()
})
watch(searchString, (newVal, oldVal) => {
  if (newVal !== oldVal) refreshListDebounce()
})
watch(
  selectedObject,
  async () => {
    refreshList()
    piniaStore.setSelectedLink(null)
  },
  { immediate: true }
)
onUnmounted(() => {
  piniaStore.setSelectedLink(null)
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.overlay-confirm-delete {
  left: -72px;
  top: -16px;
  &__mobile,
  &__fullscreen {
    left: 0;
    top: 0;
  }
}
#links-dashboard {
  background-color: #fff;
  border-bottom-right-radius: variables.$border-radius-small;
  border-bottom-left-radius: variables.$border-radius-small;
  display: flex;
  flex-flow: column;
  height: fit-content;
  max-height: calc(
    100vh - #{variables.$layout-top-bar-height} - #{variables.$padding-xlarge} - 152px
  );
  min-height: 400px;
  overflow: hidden;
  width: 100%;
  &.mobile {
    border-radius: 0;
    bottom: 0;
    height: 100%;
    left: 0;
    position: fixed;
    right: 0;
    top: calc(#{variables.$layout-top-bar-height} + 56px);
    max-height: calc(100vh - #{variables.$layout-top-bar-height} - 56px);
    width: 100%;
  }
}
.bottom-container {
  background-color: #fff;
  border-top: 1px solid variables.$color-greyscale-line-grey;
  display: flex;
  flex: 0 0 72px;
  flex-flow: column;
  height: 72px;
  justify-self: flex-end;
  padding: 16px variables.$padding-main-body;
  &__no-button {
    border-top: none;
    flex: 0 0 24px;
    height: 24px;
    padding: 0;
  }
}
.include-child-object-container {
  align-items: top;
  display: flex;
  flex: 0 0 40px;
  height: 40px;
  padding: 0 24px;
  &__checkbox {
    margin-left: -3px;
    margin-right: 5px;
    padding: 0;
  }
  &__checkbox-placeholder {
    border-radius: 4px;
    border: 1px solid #767676;
    cursor: not-allowed;
    height: 18px;
    margin-right: 8px;
    margin-top: 3px;
    width: 18px;
  }
  &__info {
    height: 24px;
  }
  &__text {
    color: variables.$color-text-secondary;
    padding-right: 4px;
    @include font-mixins.desktop-regular-paragraph;
  }
}
.table-container {
  display: contents;
}
.search-container {
  display: flex;
  flex: 0 0 64px;
  flex-flow: column;
  height: 64px;
  &__input {
    margin-top: 8px;
    padding: 0 24px;
    :deep(.text-field-standard) {
      height: 40px;
    }
  }
}
</style>
